import { parseDebtorCreditorFile } from "@/apis/custom/customRoutes";
import { Label } from "@/components/ui/label";
import { useSession } from "@/lib/auth/auth";
import { cn } from "@/lib/utils";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { FileText, Loader2, Upload, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { FormValues } from "../form-schema";

interface DebtorCreditorFileUploadProps {
  label: string;
  accept: string;
  onChange: (file: File | null) => void;
  onDebtorsCreditorsParsed: (
    debtorsCreditors: Awaited<ReturnType<typeof parseDebtorCreditorFile>>,
  ) => void;
  value?: File | null;
}

export function DebtorCreditorFileUpload({
  label,
  accept,
  onChange,
  onDebtorsCreditorsParsed,
  value,
}: DebtorCreditorFileUploadProps) {
  const [dragActive, setDragActive] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [debtorCreditorCount, setDebtorCreditorCount] = useState<{
    debtors: number;
    creditors: number;
  } | null>(null);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const { data: session } = useSession();
  const { t } = useTranslation();
  const { getValues } = useFormContext<FormValues>();

  // Update local state when value prop changes (for persistence between steps)
  useEffect(() => {
    if (value) {
      setFile(value);
      setParsingError(null);
      // Parse the file when it's set
      parseFile(value);
    } else {
      setFile(null);
      setDebtorCreditorCount(null);
      // Don't clear parsingError here - let it persist to show the error message
    }
  }, [value]);

  const parseFile = async (fileToParse: File) => {
    setIsParsing(true);
    setParsingError(null);
    try {
      const data = await parseDebtorCreditorFile(
        fileToParse,
        getValues().companyName,
      );

      if (data.debtors.length === 0 && data.creditors.length === 0) {
        // No debtors or creditors found - treat as error
        const errorMessage = t(
          "mandanten_create.debtor_creditor_file_upload.file_processing_error",
          { filename: fileToParse.name },
        );
        setParsingError(errorMessage);
        toast.error(
          t("mandanten_create.debtor_creditor_file_upload.parsing_error"),
        );

        // Remove the file but keep the error state
        setFile(null);
        setDebtorCreditorCount(null);
        onChange(null);

        // Reset the file input value
        const fileInput = document.getElementById(
          `file-${label}`,
        ) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
        return;
      }

      setDebtorCreditorCount({
        debtors: data.debtors.length,
        creditors: data.creditors.length,
      });
      toast.success(
        t(
          "mandanten_create.debtor_creditor_file_upload.debtors_creditors_found",
          {
            debtors: data.debtors.length,
            creditors: data.creditors.length,
          },
        ),
      );

      onDebtorsCreditorsParsed(data);
    } catch (error) {
      console.error("Error parsing debtor creditor file:", error);
      const errorMessage = t(
        "mandanten_create.debtor_creditor_file_upload.file_processing_error",
        { filename: fileToParse.name },
      );
      setParsingError(errorMessage);
      toast.error(
        t("mandanten_create.debtor_creditor_file_upload.parsing_error"),
      );

      // Remove the file but keep the error state
      setFile(null);
      setDebtorCreditorCount(null);
      onChange(null);

      // Reset the file input value
      const fileInput = document.getElementById(
        `file-${label}`,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const newFile = e.dataTransfer.files?.[0];
      setFile(newFile);
      onChange(newFile);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files?.[0]) {
      const newFile = e.target.files?.[0];
      setFile(newFile);
      onChange(newFile);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFile(null);
    setDebtorCreditorCount(null);
    onChange(null);

    // Reset the file input value
    const fileInput = document.getElementById(
      `file-${label}`,
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      {!file ? (
        // Upload interface when no file is selected
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer",
            dragActive ? "border-primary" : "border-gray-300",
            "hover:border-primary transition-colors",
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept={accept}
            onChange={handleChange}
            className="hidden"
            id={`file-${label}`}
          />
          <label
            htmlFor={`file-${label}`}
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="h-6 w-6 mb-2 text-gray-500" />
            <span className="text-sm font-medium">
              <strong>
                {t(
                  "mandanten_create.debtor_creditor_file_upload.click_or_drag",
                )}
              </strong>
            </span>
          </label>
        </div>
      ) : (
        // File preview when a file is selected
        <div className="border rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 p-2 rounded">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium truncate-short max-w-[200px]">
                  {file.name}
                </span>
                <span className="text-xs text-gray-500">
                  {formatFileSize(file.size)}
                </span>
                {isParsing && (
                  <div className="flex items-center space-x-2 mt-1">
                    <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                    <span className="text-xs text-blue-500">
                      {t(
                        "mandanten_create.debtor_creditor_file_upload.analyzing",
                      )}
                    </span>
                  </div>
                )}
                {debtorCreditorCount !== null && !isParsing && (
                  <>
                    <span
                      className={cn(
                        "text-xs font-medium mt-1",
                        debtorCreditorCount.debtors < 50
                          ? "text-yellow-600"
                          : "text-green-600",
                      )}
                    >
                      {t(
                        "mandanten_create.debtor_creditor_file_upload.debtors_found",
                        { count: debtorCreditorCount.debtors },
                      )}
                    </span>
                    <span
                      className={cn(
                        "text-xs font-medium mt-1",
                        debtorCreditorCount.creditors < 50
                          ? "text-yellow-600"
                          : "text-green-600",
                      )}
                    >
                      {t(
                        "mandanten_create.debtor_creditor_file_upload.creditors_found",
                        { count: debtorCreditorCount.creditors },
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={handleRemoveFile}
              className="p-1 rounded-full hover:bg-gray-100"
              title={t(
                "mandanten_create.debtor_creditor_file_upload.remove_file",
              )}
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {/* Prominent warning card for low debtor/creditor count */}
      {debtorCreditorCount !== null && !isParsing && (
        <>
          <LowOrNoDebtorCreditorCount
            type="debtor"
            count={debtorCreditorCount.debtors}
          />
          <LowOrNoDebtorCreditorCount
            type="creditor"
            count={debtorCreditorCount.creditors}
          />
        </>
      )}

      {/* Error warning card for parsing failures */}
      {parsingError && (
        <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 mb-1">
                {t(
                  "mandanten_create.debtor_creditor_file_upload.file_processing_error_title",
                )}
              </h4>
              <p className="text-sm text-red-700">{parsingError}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LowOrNoDebtorCreditorCount({
  type,
  count,
}: {
  type: "debtor" | "creditor";
  count: number;
}) {
  const LOW_COUNT_THRESHOLD = 50;

  const { t } = useTranslation();

  if (count >= LOW_COUNT_THRESHOLD) {
    return null;
  }

  const { title, description } = (
    {
      debtor: {
        no: {
          title:
            "mandanten_create.debtor_creditor_file_upload.no_debtors_title",
          description:
            "mandanten_create.debtor_creditor_file_upload.no_debtors_description",
        },
        low: {
          title:
            "mandanten_create.debtor_creditor_file_upload.low_debtor_count_title",
          description:
            "mandanten_create.debtor_creditor_file_upload.low_debtor_count_description",
        },
      },
      creditor: {
        no: {
          title:
            "mandanten_create.debtor_creditor_file_upload.no_creditors_title",
          description:
            "mandanten_create.debtor_creditor_file_upload.no_creditors_description",
        },
        low: {
          title:
            "mandanten_create.debtor_creditor_file_upload.low_creditor_count_title",
          description:
            "mandanten_create.debtor_creditor_file_upload.low_creditor_count_description",
        },
      },
    } as const
  )[type][count === 0 ? "no" : "low"];

  return (
    <div className="mt-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-orange-800 mb-1">
            {t(title, { count })}
          </h4>
          <p className="text-sm text-orange-700">{t(description)}</p>
        </div>
      </div>
    </div>
  );
}
